import React, { useEffect, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Activity, AlertTriangle, CheckCircle2, XCircle, Copy, ChevronLeft, ChevronRight, Wifi, WifiOff, BarChart3 } from 'lucide-react';

const statusConfig = {
  processed: { label: 'Processado', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  duplicate: { label: 'Duplicado', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Copy },
  ignored: { label: 'Ignorado', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: XCircle },
  error: { label: 'Erro', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
};

export default function WebhookLogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ daily: [], by_instance: [] });
  const [disconnections, setDisconnections] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterInstance, setFilterInstance] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('logs');

  useEffect(() => { loadLogs(); loadStats(); loadDisconnections(); }, []);
  useEffect(() => { loadLogs(); }, [page, filterStatus, filterInstance]);

  const loadLogs = async () => {
    try {
      const params = { page, limit: 50 };
      if (filterStatus) params.status = filterStatus;
      if (filterInstance) params.instance = filterInstance;
      const r = await api.getWebhookLogs(params);
      setLogs(r.data.logs || []);
      setTotalPages(r.data.pages || 1);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const r = await api.getWebhookStats(7);
      setStats(r.data);
    } catch { /* ignore */ }
  };

  const loadDisconnections = async () => {
    try {
      const r = await api.getDisconnectionHistory(30);
      setDisconnections(r.data.disconnections || []);
    } catch { /* ignore */ }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  if (loading) return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="webhook-logs-title">
              Webhook Logs
            </h1>
            <p className="text-slate-600 mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Monitore todas as mensagens recebidas para mapear leads do Facebook Ads
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6" data-testid="webhook-tabs">
            <Button variant={tab === 'logs' ? 'default' : 'outline'} onClick={() => setTab('logs')} className={tab === 'logs' ? 'bg-emerald-600 hover:bg-emerald-700' : ''} data-testid="tab-logs">
              <Activity className="w-4 h-4 mr-2" /> Logs
            </Button>
            <Button variant={tab === 'stats' ? 'default' : 'outline'} onClick={() => setTab('stats')} className={tab === 'stats' ? 'bg-emerald-600 hover:bg-emerald-700' : ''} data-testid="tab-stats">
              <BarChart3 className="w-4 h-4 mr-2" /> Estatisticas
            </Button>
            <Button variant={tab === 'disconnections' ? 'default' : 'outline'} onClick={() => setTab('disconnections')} className={tab === 'disconnections' ? 'bg-emerald-600 hover:bg-emerald-700' : ''} data-testid="tab-disconnections">
              <WifiOff className="w-4 h-4 mr-2" /> Desconexoes
            </Button>
          </div>

          {/* Logs Tab */}
          {tab === 'logs' && (
            <>
              {/* Filters */}
              <Card className="p-4 mb-4">
                <div className="flex items-center gap-4">
                  <Select value={filterStatus || 'all'} onValueChange={v => { setPage(1); setFilterStatus(v === 'all' ? '' : v); }}>
                    <SelectTrigger className="w-48" data-testid="filter-log-status">
                      <SelectValue placeholder="Todos os status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="processed">Processados</SelectItem>
                      <SelectItem value="duplicate">Duplicados</SelectItem>
                      <SelectItem value="ignored">Ignorados</SelectItem>
                      <SelectItem value="error">Erros</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterInstance || 'all'} onValueChange={v => { setPage(1); setFilterInstance(v === 'all' ? '' : v); }}>
                    <SelectTrigger className="w-48" data-testid="filter-log-instance">
                      <SelectValue placeholder="Todas instancias" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {stats.by_instance?.map(i => (
                        <SelectItem key={i.instance} value={i.instance}>{i.instance}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-slate-500 ml-auto">{total} registros</span>
                </div>
              </Card>

              {/* Table */}
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Instancia</TableHead>
                      <TableHead>Produto</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Motivo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12 text-slate-400">
                          Nenhum webhook registrado ainda. Os logs aparecerao quando mensagens chegarem.
                        </TableCell>
                      </TableRow>
                    ) : logs.map((log, i) => {
                      const sc = statusConfig[log.status] || statusConfig.ignored;
                      const Icon = sc.icon;
                      return (
                        <TableRow key={i}>
                          <TableCell className="text-xs text-slate-600 whitespace-nowrap">{formatDate(log.received_at)}</TableCell>
                          <TableCell className="text-sm font-medium">{log.instance || '-'}</TableCell>
                          <TableCell className="text-sm">{log.product || '-'}</TableCell>
                          <TableCell className="text-sm font-mono">{log.phone || '-'}</TableCell>
                          <TableCell className="text-sm">{log.push_name || '-'}</TableCell>
                          <TableCell>
                            <Badge className={`${sc.color} border text-xs`}>
                              <Icon className="w-3 h-3 mr-1" />
                              {sc.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 max-w-48 truncate">{log.reason || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-slate-500">Pagina {page} de {totalPages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="logs-prev">
                      <ChevronLeft className="w-4 h-4" /> Anterior
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="logs-next">
                      Proximo <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Stats Tab */}
          {tab === 'stats' && (
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-slate-800 mb-4">Webhooks por Dia (ultimos 7 dias)</h2>
                {stats.daily?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Processados</TableHead>
                        <TableHead>Duplicados</TableHead>
                        <TableHead>Ignorados</TableHead>
                        <TableHead>Erros</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stats.daily.map(d => (
                        <TableRow key={d.date}>
                          <TableCell className="font-medium">{d.date}</TableCell>
                          <TableCell className="font-bold">{d.total}</TableCell>
                          <TableCell className="text-emerald-600">{d.processed || 0}</TableCell>
                          <TableCell className="text-amber-600">{d.duplicate || 0}</TableCell>
                          <TableCell className="text-slate-500">{d.ignored || 0}</TableCell>
                          <TableCell className="text-red-600">{d.error || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-slate-400 text-center py-8">Nenhum dado nos ultimos 7 dias</p>
                )}
              </Card>

              <Card className="p-6">
                <h2 className="text-lg font-semibold text-slate-800 mb-4">Webhooks por Instancia</h2>
                {stats.by_instance?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {stats.by_instance.map(inst => (
                      <div key={inst.instance} className="p-4 rounded-lg border bg-white">
                        <p className="font-medium text-sm">{inst.instance}</p>
                        <p className="text-2xl font-bold text-slate-800 mt-1">{inst.total}</p>
                        <p className="text-xs text-emerald-600">{inst.processed} processados</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-8">Nenhum dado disponivel</p>
                )}
              </Card>
            </div>
          )}

          {/* Disconnections Tab */}
          {tab === 'disconnections' && (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Instancia</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead>Evento</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {disconnections.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-12 text-slate-400">
                        Nenhuma desconexao registrada nos ultimos 30 dias
                      </TableCell>
                    </TableRow>
                  ) : disconnections.map((d, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{formatDate(d.detected_at)}</TableCell>
                      <TableCell className="font-medium">{d.instance_name}</TableCell>
                      <TableCell>{d.product}</TableCell>
                      <TableCell>
                        <Badge className="bg-red-100 text-red-700 border-red-200 border text-xs">
                          <WifiOff className="w-3 h-3 mr-1" /> Desconectado
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
